import EssenceAnimatedButton from '../../../components/essence/button/animated-button';
import Header from '../../../shared/components/header/page';
import Content from '../../../shared/components/content/page';
import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import accountService from '../../../shared/services/account-service';
import getMemberType from '../../../secure/circle-details/member-type';
import { useCircleQueries } from '../../../shared/tanstack/circle/queries';
import { useQuery } from '@tanstack/react-query';
import ParticipantsList from '../components/participant-list/page';
import { InvalidCircleIdError } from '../../../shared/models/errors/invalid-circle-id-error';
import WarningMessage from '../components/warning-message/page';
import { ContextExpenseUser, useExpenseContext } from '../../../contexts/expense/expense-context';
import amountService from '../../../shared/services/amount.service';

const AddEditExpenseStep2 = () => {
	const { id: circleId } = useParams();
	const navigate = useNavigate();
	const { circleQuery, participantsQuery } = useCircleQueries();
	if (!circleId) {
		throw new InvalidCircleIdError();
	}
	const { data: circle } = useQuery(circleQuery(circleId));
	const { data: circleParticipants } = useQuery(participantsQuery(circleId));
	const { expense, setExpense } = useExpenseContext();

	const [participants, setParticipants] = useState<ContextExpenseUser[]>();

	let member: string | undefined;
	const account = accountService.getAccount();
	const loggedInUserId = account?.id;

	if (circle) {
		const memberType = getMemberType(circle.type);
		if (memberType) {
			member = memberType.pluralTitle;
		}
	}

	useEffect(() => {
		if (circleParticipants && loggedInUserId) {
			if (expense?.creditors && expense.creditors.length === 0) {
				expense.creditors.push({
					id: loggedInUserId,
					amount: expense.amount,
					currency: (expense.currency?.length ?? 0) > 0 ? expense.currency : '₹',
					circleId: circleId,
				});
			}
			const updatedParticipants = circleParticipants.map(participant => {
				const creditor = expense?.creditors?.find(creditor => creditor.id === participant.id);
				const isChecked = creditor ? true : false;
				const newAmount = creditor ? creditor.amount : isChecked ? expense?.amount : 0;
				return {
					...participant,
					isChecked,
					amount: newAmount,
					currency: (expense?.currency?.length ?? 0) > 0 ? expense?.currency : '₹',
				};
			});

			setParticipants(updatedParticipants);
		}
	}, [circleId, circleParticipants, expense?.amount, expense?.creditors, expense?.currency, loggedInUserId]);

	const debitAmount = useMemo(
		() => participants?.reduce((sum, participant) => sum + amountService.getAmount(participant.amount), 0) ?? 0,
		[participants],
	);

	const goToParticipants = () => {
		const creditors = participants?.filter(participant => amountService.getAmount(participant.amount) > 0);
		if (expense) {
			setExpense({ ...expense, creditors });
		}
		navigate(`/secure/circles/${circleId}/add-expense/participants`);
	};

	const form = {
		get isDisabled() {
			return amountService.getAmount(expense?.amount) - debitAmount !== 0 ? true : undefined;
		},
	};

	return (
		<>
			{member && <Header title="Paid by" subtitle={`Select the list of ${member} who paid for the expense.`} />}
			<Content>
				<ul>
					{participants && (
						<ParticipantsList
							currency={(expense?.currency?.length ?? 0) > 0 ? (expense?.currency ?? '₹') : '₹'}
							totalAmount={amountService.getAmount(expense?.amount)}
							circleParticipants={participants}
							onChange={setParticipants}
							loggedInUserId={loggedInUserId ?? ''}
						/>
					)}
					{form.isDisabled && (
						<WarningMessage amount1={amountService.getAmount(expense?.amount)} amount2={debitAmount} />
					)}
					<EssenceAnimatedButton onClick={goToParticipants} name="next" disabled={form.isDisabled} />
				</ul>
			</Content>
		</>
	);
};

export default AddEditExpenseStep2;
